import { kernelCanonicalJson, sha256DigestOf, sha256DigestSchema, sriSha384Schema } from "./kernel-encoding.js";
import { KERNEL_KID_PATTERN, LOWER_UUID_PATTERN, base64UrlBytesOrNull, canonicalByteLength, importEs256VerifyKey, isStrictlyAscending, verifyEs256 } from "./kernel-internal.js";
import { kernelRightListSchema, sortKernelRights } from "./kernel-rights.js";
import { z } from "zod";
import { utf8ToBytes } from "@noble/hashes/utils.js";
//#region src/kernel/release-statement.ts
/**
* The release statement: the developer-signed list of what one app version runs (kernel apps,
* WS-1a, decisions 7 and 9, R-042).
*
* A release statement names one app version: its bundle digest, each image, each UI file and the
* kernel rights it asks for. `zion-app release` (WS-24) builds it with
* {@link buildReleaseStatement}, and the code-signing key signs the canonical bytes. The CP publish
* door (WS-8) checks the signature with {@link verifyReleaseStatementSignature} and compares the
* digests with the OCI bundle. The rights certificate (`rights-certificate.ts`) then carries the
* same image and UI file entries to every DP.
*
* Every bound in this module keeps a certificate under the 65,536-character wire cap (R-005):
*
* 1. The certificate header is at most 188 bytes, so 251 base64url characters. The signature takes 86.
* 2. The claims hold at most 1,024 bytes more than the statement.
* 3. A statement of 47,104 bytes gives claims of at most 48,128 bytes, so 64,171 characters and a token of 64,510.
* 4. A UI entry takes at least 96 canonical bytes, and 47,104 / 96 is 490. So the UI file cap is 480.
*
* Browser-safe: no Node builtin and no Node global. Signature checks use the WebCrypto global
* `crypto.subtle`, which Node 24 and every browser define. No exported signature names a DOM type.
*/
/** The `typ` member of every release statement. */
const RELEASE_STATEMENT_TYP = "zion-release-statement";
/** The largest canonical JSON text of a release statement, in UTF-8 bytes (R-005). */
const RELEASE_STATEMENT_MAX_BYTES = 47104;
/** The most images one statement names. */
const KERNEL_IMAGES_MAX = 64;
/** The most platform digests one image entry names, for a multi-arch image (WS-1 open question 7). */
const KERNEL_PLATFORM_DIGESTS_MAX = 8;
/** The most UI files one statement names (R-005). */
const KERNEL_UI_FILES_MAX = 480;
/**
* The component of an image entry: `fixture:<id>` or `pool:<name>`, a lower-case word of 1 to 63
* characters of `[a-z0-9-]`. The first character follows the grammar of the named thing:
*
* 1. A fixture id starts with a letter, as the `fixtureId` pattern of `fixture.schema.json` does.
* 2. A pool name starts with a letter or a digit, as `WORK_NAME_PATTERN` (the app manifest `pools`
*    key) does. So `pool:3d-render` is a valid component.
*/
const KERNEL_COMPONENT_PATTERN = /^(?:fixture:[a-z]|pool:[a-z0-9])[a-z0-9-]{0,62}$/;
/**
* The path of a UI file, relative to the bundle root. Segments of `[A-Za-z0-9._-]` between single
* `/` characters. It refuses a leading `/`, a trailing `/`, `//`, a `.` or `..` segment, a
* backslash and every other character. The schemas apply `.max(KERNEL_UI_PATH_MAX)` before it.
*/
const KERNEL_UI_PATH_PATTERN = /^(?!\.{1,2}(?:\/|$))(?!.*\/\.{1,2}(?:\/|$))[A-Za-z0-9._-]+(?:\/[A-Za-z0-9._-]+)*$/;
/** The longest UI file path, in characters. */
const KERNEL_UI_PATH_MAX = 256;
const APP_SLUG_PATTERN = /^[a-z][a-z0-9-]*[a-z0-9]$/;
const APP_VERSION_PATTERN = /^\d+\.\d+\.\d+/;
const BASE64URL_PATTERN = /^[A-Za-z0-9_-]+$/;
/** The zod form of {@link KernelImageEntry}: strict, with bounded and sorted platform digests. */
const kernelImageEntrySchema = z.strictObject({
	component: z.string().max(70).regex(KERNEL_COMPONENT_PATTERN),
	digest: sha256DigestSchema,
	platformDigests: z.array(sha256DigestSchema).max(8).refine(isStrictlyAscending, { message: "platform digests must be sorted with no duplicate" })
});
/** The zod form of {@link KernelUiFileEntry}: strict, with a bounded path. */
const kernelUiFileEntrySchema = z.strictObject({
	path: z.string().max(KERNEL_UI_PATH_MAX).regex(KERNEL_UI_PATH_PATTERN),
	sha384: sriSha384Schema
});
/**
* The image list rule that the certificate claims share: bounded, in component order, unique.
* Internal to `@zion/sdk-contracts`: the root barrel does not re-export it.
*/
const kernelImageListSchema = z.array(kernelImageEntrySchema).max(64).refine((images) => isStrictlyAscending(images.map((image) => image.component)), { message: "images must be in component order with no duplicate component" });
/**
* The UI file list rule that the certificate claims share: bounded, in path order, unique.
* Internal to `@zion/sdk-contracts`: the root barrel does not re-export it.
*/
const kernelUiFileListSchema = z.array(kernelUiFileEntrySchema).max(480).refine((files) => isStrictlyAscending(files.map((file) => file.path)), { message: "UI files must be in path order with no duplicate path" });
/**
* The zod form of {@link ReleaseStatement}. Strict at every level, with `.max()` before each
* `.regex()`. It refuses these inputs:
*
* 1. more than {@link KERNEL_IMAGES_MAX} images, {@link KERNEL_PLATFORM_DIGESTS_MAX} digests per
*    image or {@link KERNEL_UI_FILES_MAX} UI files;
* 2. a duplicate component, or images out of component order;
* 3. a duplicate path, or UI files out of path order;
* 4. a UI path that {@link KERNEL_UI_PATH_PATTERN} refuses, or one over 256 characters;
* 5. kernel rights that `kernelRightListSchema` refuses;
* 6. a canonical JSON text over {@link RELEASE_STATEMENT_MAX_BYTES} in UTF-8.
*
* It never sorts. Use {@link buildReleaseStatement} to sort an input.
*/
const releaseStatementSchema = z.strictObject({
	v: z.literal(1),
	typ: z.literal(RELEASE_STATEMENT_TYP),
	appId: z.string().max(36).regex(LOWER_UUID_PATTERN),
	appSlug: z.string().max(30).regex(APP_SLUG_PATTERN),
	version: z.string().max(64).regex(APP_VERSION_PATTERN),
	bundleDigest: sha256DigestSchema,
	images: kernelImageListSchema,
	uiFiles: kernelUiFileListSchema,
	kernelRights: kernelRightListSchema
}).superRefine((statement, ctx) => {
	if (canonicalByteLength(statement) > 47104) ctx.addIssue({
		code: "custom",
		path: [],
		message: `the canonical release statement exceeds ${RELEASE_STATEMENT_MAX_BYTES} bytes`
	});
});
/**
* The error of {@link buildReleaseStatement}. `issues` holds one zod issue path per broken bound,
* each path joined with `.`, and `(root)` for the statement as a whole (the byte cap).
*/
var ReleaseStatementError = class extends Error {
	issues;
	constructor(issues) {
		super(`release statement refused: ${issues.join(", ")}`);
		this.name = "ReleaseStatementError";
		this.issues = issues;
	}
};
/**
* The release statement of `input` in canonical form. It sorts the images by component, the
* platform digests, the UI files by path and the kernel rights (with {@link sortKernelRights},
* which drops a duplicate right), then parses with {@link releaseStatementSchema}. It throws
* {@link ReleaseStatementError} with the issue paths for a broken bound, a duplicate component or
* path, or an unknown right. It never throws for order alone. Pure.
*/
function buildReleaseStatement(input) {
	const candidate = {
		v: 1,
		typ: RELEASE_STATEMENT_TYP,
		appId: input.appId,
		appSlug: input.appSlug,
		version: input.version,
		bundleDigest: input.bundleDigest,
		images: input.images.map((image) => ({
			component: image.component,
			digest: image.digest,
			platformDigests: [...image.platformDigests].sort()
		})).sort((a, b) => a.component < b.component ? -1 : a.component > b.component ? 1 : 0),
		uiFiles: input.uiFiles.map((file) => ({
			path: file.path,
			sha384: file.sha384
		})).sort((a, b) => a.path < b.path ? -1 : a.path > b.path ? 1 : 0),
		kernelRights: sortKernelRights(input.kernelRights)
	};
	const parsed = releaseStatementSchema.safeParse(candidate);
	if (!parsed.success) throw new ReleaseStatementError(parsed.error.issues.map((issue) => issue.path.length === 0 ? "(root)" : issue.path.map(String).join(".")));
	return parsed.data;
}
/** The canonical JSON text of `statement`, the bytes that the code-signing key signs. Pure. */
function releaseStatementCanonicalJson(statement) {
	return kernelCanonicalJson(statement);
}
/** The `sha256:` digest of the UTF-8 bytes of {@link releaseStatementCanonicalJson}. Pure. */
function releaseStatementDigest(statement) {
	return sha256DigestOf(utf8ToBytes(releaseStatementCanonicalJson(statement)));
}
/**
* The zod form of {@link ReleaseStatementSignature}. Strict. `sig` is base64url text of at most 128
* characters; {@link verifyReleaseStatementSignature} then requires 64 decoded bytes.
*/
const releaseStatementSignatureSchema = z.strictObject({
	alg: z.literal("ES256"),
	kid: z.string().max(128).regex(KERNEL_KID_PATTERN),
	sig: z.string().max(128).regex(BASE64URL_PATTERN)
});
/**
* Checks a developer signature over a release statement. Untrusted input. The checks run in this
* order, and the first failure gives the answer:
*
* 1. `codeSigningKey` must be a public ES256 P-256 key with `y` that WebCrypto imports. Otherwise `unsupported_key`.
* 2. `signature` must parse with {@link releaseStatementSignatureSchema}, and `sig` must decode to 64 bytes. Otherwise `malformed`.
* 3. `signature.kid` must equal `codeSigningKey.kid`. Otherwise `kid_mismatch`.
* 4. The canonical text of `statement` must be at most {@link RELEASE_STATEMENT_MAX_BYTES}. Otherwise `oversize`; a value that canonical JSON refuses is `malformed`.
* 5. `statement` must parse with {@link releaseStatementSchema}. Otherwise `malformed`. It never sorts an untrusted statement.
* 6. WebCrypto must verify the signature over the canonical bytes. Otherwise `bad_signature`.
*
* It answers `ok: true` with the statement digest only for a valid signature. It never rejects for
* caller input, reads no clock and writes nothing.
*/
async function verifyReleaseStatementSignature(statement, signature, codeSigningKey) {
	const key = await importEs256VerifyKey(codeSigningKey);
	if (key === null) return {
		ok: false,
		reason: "unsupported_key"
	};
	const parsedSignature = releaseStatementSignatureSchema.safeParse(signature);
	if (!parsedSignature.success) return {
		ok: false,
		reason: "malformed"
	};
	const sigBytes = base64UrlBytesOrNull(parsedSignature.data.sig);
	if (sigBytes?.length !== 64) return {
		ok: false,
		reason: "malformed"
	};
	if (parsedSignature.data.kid !== codeSigningKey.kid) return {
		ok: false,
		reason: "kid_mismatch"
	};
	let size;
	try {
		size = canonicalByteLength(statement);
	} catch {
		return {
			ok: false,
			reason: "malformed"
		};
	}
	if (size > 47104) return {
		ok: false,
		reason: "oversize"
	};
	const parsedStatement = releaseStatementSchema.safeParse(statement);
	if (!parsedStatement.success) return {
		ok: false,
		reason: "malformed"
	};
	const bytes = utf8ToBytes(releaseStatementCanonicalJson(parsedStatement.data));
	if (!await verifyEs256(key, sigBytes, bytes)) return {
		ok: false,
		reason: "bad_signature"
	};
	return {
		ok: true,
		digest: sha256DigestOf(bytes)
	};
}
//#endregion
export { KERNEL_COMPONENT_PATTERN, KERNEL_IMAGES_MAX, KERNEL_PLATFORM_DIGESTS_MAX, KERNEL_UI_FILES_MAX, KERNEL_UI_PATH_PATTERN, RELEASE_STATEMENT_MAX_BYTES, RELEASE_STATEMENT_TYP, ReleaseStatementError, buildReleaseStatement, kernelImageListSchema, kernelUiFileListSchema, releaseStatementCanonicalJson, releaseStatementDigest, releaseStatementSchema, releaseStatementSignatureSchema, verifyReleaseStatementSignature };

//# sourceMappingURL=release-statement.js.map